import {
  aiMimeTypes,
  ALINE_TOOL_NAMES,
  PlanStateSchema,
  ReportAiGenerationResultSchema,
  toolCallSchema,
  uiToolPartSchema,
} from '@aline/common';
import type { Apollo } from 'src/gql/generated';
import { getIsPlainRecord } from 'src/utils/get-is-plain-record';

import {
  addMentionEntityIdsFromMarkdown,
  createMentionEntityIdSets,
  toMentionEntityIds,
} from './mention-entity-id-extraction';
import type { MentionEntityIds } from './useMentionEntitiesQuery';
import { getSkillLoadVersionIds } from '../ChatPanel/skill-load-version-ids';

const TOOL_CALL_FENCE_REGEX = /```json-tool-call\s*\n([\s\S]*?)\n```/g;
const REPORT_GENERATION_FENCE_REGEX = new RegExp(
  `\`\`\`${aiMimeTypes.jsonReportGeneration}\\s*\\n([\\s\\S]*?)\\n\`\`\``,
  'g'
);

export function extractMentionEntityIdsFromMessages(
  messages: (Pick<Apollo.AgentMessage, 'planState' | 'text'> &
    Partial<Pick<Apollo.AgentMessage, 'data'>>)[]
): MentionEntityIds {
  const allIds = createMentionEntityIdSets();

  for (const message of messages) {
    addMentionEntityIdsFromMarkdown(allIds, message.text);
    addMentionEntityIdsFromToolCalls(allIds, message.text);
    addMentionEntityIdsFromReportGenerationFences(allIds, message.text);
    addMentionEntityIdsFromUiParts(allIds, message.data);

    if (!message.planState) continue;
    const parsedPlanState = PlanStateSchema.safeParse(message.planState);
    if (!parsedPlanState.success) continue;

    addMentionEntityIdsFromMarkdown(allIds, parsedPlanState.data.plannerText);
    addMentionEntityIdsFromToolCalls(allIds, parsedPlanState.data.plannerText);
    addMentionEntityIdsFromReportGenerationFences(allIds, parsedPlanState.data.plannerText);

    for (const nodeOutput of Object.values(parsedPlanState.data.nodeOutputs)) {
      addMentionEntityIdsFromMarkdown(allIds, nodeOutput.displayText);
      addMentionEntityIdsFromToolCalls(allIds, nodeOutput.displayText);
      addMentionEntityIdsFromReportGenerationFences(allIds, nodeOutput.displayText);
      for (const artifact of nodeOutput.artifacts ?? []) {
        if (artifact.kind === 'createdReport') allIds.reportIds.add(artifact.reportId);
        if (artifact.kind === 'createdWorkflow') allIds.workflowIds.add(artifact.workflowId);
      }
    }
  }

  return toMentionEntityIds(allIds);
}

function addMentionEntityIdsFromUiParts(
  allIds: ReturnType<typeof createMentionEntityIdSets>,
  data: unknown
) {
  if (!getIsPlainRecord(data) || !Array.isArray(data.uiParts)) return;
  for (const versionId of getSkillLoadVersionIds(data.uiParts))
    allIds.skillVersionIds.add(versionId);

  for (const rawPart of data.uiParts) {
    const part = uiToolPartSchema.safeParse(rawPart);
    if (!part.success || !getIsPlainRecord(part.data.input)) continue;

    switch (part.data.toolName) {
      case ALINE_TOOL_NAMES.workflowReadResource: {
        const workflowId = part.data.input.workflowId;
        if (typeof workflowId === 'string') allIds.workflowIds.add(workflowId);
        break;
      }
      case ALINE_TOOL_NAMES.workflowAssignTask: {
        const taskId = part.data.input.taskId;
        if (typeof taskId === 'string') allIds.taskIds.add(taskId);
        break;
      }
    }
  }
}

function addMentionEntityIdsFromToolCalls(
  allIds: ReturnType<typeof createMentionEntityIdSets>,
  markdown: string | null | undefined
) {
  if (!markdown) return;

  for (const match of markdown.matchAll(TOOL_CALL_FENCE_REGEX)) {
    const [, rawJson] = match;
    if (!rawJson) continue;

    const parsedToolCall = parseToolCallJson(rawJson);
    if (!parsedToolCall.success) continue;

    switch (parsedToolCall.data.type) {
      case 'queryDocument':
      case 'readDocument':
        allIds.documentIds.add(parsedToolCall.data.documentId);
        break;
      case 'readPlaybook':
        allIds.playbookIds.add(parsedToolCall.data.playbookId);
        break;
      case 'runPlaybook':
        allIds.documentIds.add(parsedToolCall.data.documentId);
        allIds.playbookIds.add(parsedToolCall.data.playbookId);
        break;
      case 'workflowLaunchTemplate':
        allIds.workflowIds.add(parsedToolCall.data.templateWorkflowId);
        break;
      case 'listTasks':
      case 'readIntakeForm':
        allIds.workflowIds.add(parsedToolCall.data.workflowId);
        break;
      case 'readWorkflow':
        if (parsedToolCall.data.workflowId) allIds.workflowIds.add(parsedToolCall.data.workflowId);
        break;
      case 'assignTask':
        if (parsedToolCall.data.taskId) allIds.taskIds.add(parsedToolCall.data.taskId);
        break;
      case 'createReport':
        if (parsedToolCall.data.reportId) allIds.reportIds.add(parsedToolCall.data.reportId);
        break;
      case 'loadSkill':
        allIds.skillVersionIds.add(parsedToolCall.data.versionId);
        break;
      case 'workflowCreateDraft':
        if (parsedToolCall.data.result?.status === 'created_draft')
          allIds.workflowIds.add(parsedToolCall.data.result.workflow.workflowId);
        break;
      case 'askUser':
      case 'calculator':
      case 'commentOnTask':
      case 'compactPriorOutput':
      case 'completeTask':
      case 'getTask':
      case 'getTaskAgentThread':
      case 'getWebPageContents':
      case 'managePhases':
      case 'modelFallback':
      case 'reassignTask':
      case 'research':
      case 'search':
      case 'searchPlaybooks':
      case 'selectDocuments':
      case 'semanticSearch':
      case 'swapAgent':
      case 'updateIntakeForm':
      case 'updateTaskDescription':
      case 'workflowCollectIntakeForm':
      case 'workflowSearchExisting':
      case 'workflowSearchTemplates':
        break;
    }
  }
}

function addMentionEntityIdsFromReportGenerationFences(
  allIds: ReturnType<typeof createMentionEntityIdSets>,
  markdown: string | null | undefined
) {
  if (!markdown) return;

  for (const match of markdown.matchAll(REPORT_GENERATION_FENCE_REGEX)) {
    const [, rawJson] = match;
    if (!rawJson) continue;

    const parsedResult = parseReportGenerationJson(rawJson);
    if (parsedResult.success) allIds.reportIds.add(parsedResult.data.reportId);
  }
}

function parseToolCallJson(rawJson: string) {
  try {
    return toolCallSchema.safeParse(JSON.parse(rawJson));
  } catch {
    return { success: false } as const;
  }
}

function parseReportGenerationJson(rawJson: string) {
  try {
    return ReportAiGenerationResultSchema.safeParse(JSON.parse(rawJson));
  } catch {
    return { success: false } as const;
  }
}
